import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { GlassCard } from '@/components/glass';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import type { EarningsHistoryItem, ReportTypeMeta } from '../types';

// Color palette for dynamic report type lines
const COLORS = [
  'var(--color-primary-800)',
  '#0097a7',
  '#e65100',
  '#7b1fa2',
  '#2e7d32',
  '#c62828',
];

interface EarningsChartProps {
  data: EarningsHistoryItem[];
  reportTypes?: ReportTypeMeta[];
}

export function EarningsChart({ data, reportTypes }: EarningsChartProps) {
  const { t, i18n } = useTranslation();
  const sym = useCurrencySymbol();
  const isHe = i18n.language === 'he';

  // Derive report type codes from data if not provided
  const codes = reportTypes?.map(rt => rt.code)
    || (data.length > 0 ? Object.keys(data[0].byReportType || {}) : []);

  const nameMap = new Map(reportTypes?.map(rt => [rt.code, isHe ? rt.name : rt.nameEn]) || []);

  // Flatten byReportType into top-level keys for recharts
  const chartData = data.map(item => ({
    label: item.label,
    total: item.total,
    ...item.byReportType,
  }));

  return (
    <GlassCard title={t('dashboard.earningsHistory')} padding="compact" contentClassName="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <defs>
            {codes.map((code, i) => (
              <linearGradient key={code} id={`color-${code}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v} ${sym}`}
          />
          <Tooltip
            contentStyle={{ borderRadius: 8, fontSize: 12 }}
            formatter={(value: number) => [`${value.toLocaleString()} ${sym}`]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {codes.map((code, i) => (
            <Area
              key={code}
              type="monotone"
              dataKey={code}
              name={nameMap.get(code) || code}
              stroke={COLORS[i % COLORS.length]}
              fill={`url(#color-${code})`}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </GlassCard>
  );
}
